import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";

export default function MinnesotaMap({ cities }) {
  const navigate = useNavigate();
  const [hoveredRegion, setHoveredRegion] = useState(null);

  // Region definitions with better paths
  const regions = [
    {
      id: "northwest-metro",
      name: "Northwest Metro",
      color: "#1E3A5F",
      hoverColor: "#D97757",
      cities: ["maple-grove", "brooklyn-park", "plymouth", "rogers", "otsego", "albertville"]
    },
    {
      id: "northeast-metro",
      name: "Northeast Metro",
      color: "#1E3A5F",
      hoverColor: "#D97757",
      cities: ["blaine", "lino-lakes", "forest-lake", "ham-lake", "coon-rapids", "andover"]
    },
    {
      id: "southwest-metro",
      name: "Southwest Metro",
      color: "#1E3A5F",
      hoverColor: "#D97757",
      cities: ["chanhassen", "eden-prairie", "shakopee", "prior-lake", "savage", "victoria", "waconia", "chaska", "hopkins", "st-louis-park"]
    },
    {
      id: "south-metro",
      name: "South Metro",
      color: "#1E3A5F",
      hoverColor: "#D97757",
      cities: ["bloomington", "burnsville", "eagan", "apple-valley", "lakeville", "farmington", "rosemount", "edina", "richfield"]
    },
    {
      id: "west-metro",
      name: "West Metro",
      color: "#1E3A5F",
      hoverColor: "#D97757",
      cities: ["minnetonka", "golden-valley", "wayzata"]
    },
    {
      id: "central",
      name: "Central Minnesota",
      color: "#152D45",
      hoverColor: "#D97757",
      cities: ["monticello", "big-lake", "elk-river"]
    },
    {
      id: "east-central",
      name: "East Central",
      color: "#152D45",
      hoverColor: "#D97757",
      cities: ["cambridge", "isanti", "north-branch", "mora"]
    },
    {
      id: "pine-county",
      name: "Pine County",
      color: "#0F2132",
      hoverColor: "#D97757",
      cities: ["hinckley", "sandstone"]
    },
    {
      id: "west-central",
      name: "West Central",
      color: "#0F2132",
      hoverColor: "#D97757",
      cities: ["willmar", "hutchinson", "glencoe", "litchfield"]
    },
    {
      id: "southwest",
      name: "Southwest Minnesota",
      color: "#0A192F",
      hoverColor: "#D97757",
      cities: ["olivia", "redwood-falls"]
    }
  ];

  const getRegionCities = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    if (!region) return [];
    return cities.filter(c => region.cities.includes(c.slug));
  };

  const handleRegionClick = (regionId) => {
    const regionCities = getRegionCities(regionId);
    if (regionCities.length > 0) {
      navigate(`/service-areas/${regionCities[0].slug}`);
    }
  };

  return (
    <div data-testid="interactive-mn-map" className="relative">
      {/* Modern Minnesota Map */}
      <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-6 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-orange rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-navy rounded-full blur-3xl" />
        </div>

        <svg
          viewBox="0 0 300 320"
          className="w-full max-w-sm mx-auto relative z-10"
          aria-label="Minnesota Service Area Map"
        >
          {/* Minnesota State Outline */}
          <defs>
            <linearGradient id="stateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0A192F" />
              <stop offset="100%" stopColor="#152D45" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* State background */}
          <path
            d="M 40 20 L 260 20 L 270 40 L 265 70 L 275 110 L 265 140 L 260 180 L 250 220 L 230 260 L 200 290 L 150 300 L 100 290 L 70 260 L 50 220 L 40 160 L 35 100 L 40 60 Z"
            fill="url(#stateGradient)"
            stroke="#1E364D"
            strokeWidth="2"
          />

          {/* Twin Cities Metro Highlight */}
          <circle
            cx="170"
            cy="170"
            r="45"
            fill={hoveredRegion ? "rgba(217, 119, 87, 0.1)" : "rgba(217, 119, 87, 0.15)"}
            stroke="#D97757"
            strokeWidth="2"
            strokeDasharray="4 2"
            className="transition-all duration-300"
          />

          {/* Metro regions as interactive dots */}
          {[
            { id: "northwest-metro", x: 145, y: 145, label: "NW" },
            { id: "northeast-metro", x: 195, y: 145, label: "NE" },
            { id: "southwest-metro", x: 145, y: 195, label: "SW" },
            { id: "south-metro", x: 185, y: 200, label: "S" },
            { id: "west-metro", x: 130, y: 170, label: "W" },
          ].map((dot) => {
            const isHovered = hoveredRegion === dot.id;
            const cityCount = getRegionCities(dot.id).length;
            
            return (
              <g
                key={dot.id}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredRegion(dot.id)}
                onMouseLeave={() => setHoveredRegion(null)}
                onClick={() => handleRegionClick(dot.id)}
              >
                <circle
                  cx={dot.x}
                  cy={dot.y}
                  r={isHovered ? 16 : 12}
                  fill={isHovered ? "#D97757" : "#1E3A5F"}
                  stroke={isHovered ? "#C26243" : "#2E4A6F"}
                  strokeWidth="2"
                  filter={isHovered ? "url(#glow)" : ""}
                  className="transition-all duration-200"
                />
                <text
                  x={dot.x}
                  y={dot.y + 4}
                  textAnchor="middle"
                  fill="white"
                  fontSize="8"
                  fontWeight="bold"
                  className="pointer-events-none"
                >
                  {cityCount}
                </text>
              </g>
            );
          })}

          {/* Greater MN regions */}
          {[
            { id: "central", x: 130, y: 110, label: "Central" },
            { id: "east-central", x: 220, y: 100, label: "E Central" },
            { id: "pine-county", x: 240, y: 60, label: "Pine" },
            { id: "west-central", x: 80, y: 150, label: "W Central" },
            { id: "southwest", x: 70, y: 220, label: "Southwest" },
          ].map((dot) => {
            const isHovered = hoveredRegion === dot.id;
            const cityCount = getRegionCities(dot.id).length;
            
            return (
              <g
                key={dot.id}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredRegion(dot.id)}
                onMouseLeave={() => setHoveredRegion(null)}
                onClick={() => handleRegionClick(dot.id)}
              >
                <circle
                  cx={dot.x}
                  cy={dot.y}
                  r={isHovered ? 14 : 10}
                  fill={isHovered ? "#D97757" : "#0F2132"}
                  stroke={isHovered ? "#C26243" : "#1E364D"}
                  strokeWidth="2"
                  filter={isHovered ? "url(#glow)" : ""}
                  className="transition-all duration-200"
                />
                <text
                  x={dot.x}
                  y={dot.y + 3}
                  textAnchor="middle"
                  fill="white"
                  fontSize="7"
                  fontWeight="bold"
                  className="pointer-events-none"
                >
                  {cityCount}
                </text>
              </g>
            );
          })}

          {/* Twin Cities marker */}
          <g className="pointer-events-none">
            <circle cx="170" cy="170" r="6" fill="#D97757" className="animate-pulse" />
            <circle cx="170" cy="170" r="3" fill="white" />
          </g>
        </svg>

        {/* Metro Label */}
        <div className="text-center mt-4">
          <span className="inline-flex items-center gap-2 bg-navy/10 text-navy px-3 py-1 rounded-full text-sm font-medium">
            <MapPin className="w-4 h-4 text-orange" />
            Twin Cities Metro
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-navy rounded-full border-2 border-navy-light" />
          <span className="text-gray-600">Metro Region</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange rounded-full" />
          <span className="text-gray-600">Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center bg-navy rounded-full text-white text-xs font-bold">5</span>
          <span className="text-gray-600">Cities Count</span>
        </div>
      </div>

      {/* Hovered Region Info */}
      {hoveredRegion && (
        <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-lg animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-heading font-semibold text-navy text-lg">
                {regions.find(r => r.id === hoveredRegion)?.name}
              </h4>
              <p className="text-gray-500 text-sm">
                {getRegionCities(hoveredRegion).length} cities served
              </p>
            </div>
            <div className="text-orange font-medium text-sm">
              Click to explore
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
